import { selectUserRbacPermissions } from 'core/userContext/selectors';
import { FC, createContext, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import noop from 'utils/noop';
import { PROJECT_PERMISSIONS } from 'utils/permissions';

export enum CREATIVE_LIBRARY_VIEWS {
  CARD = 'creative-library-view-card',
  TABLE = 'creative-library-view-table',
}

export type ViewContextState = {
  activeView: CREATIVE_LIBRARY_VIEWS;
  setActiveView: (view: string) => void;
  hasManageBaseTemplatesPermission: boolean;
};

const defaultState = {
  activeView: CREATIVE_LIBRARY_VIEWS.CARD,
  setActiveView: noop,
  hasManageBaseTemplatesPermission: false,
};

export const ViewContext = createContext<ViewContextState>(defaultState);

export const ViewContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [activeView, setActiveView] = useLocalStorage(
    'creative-library-view',
    CREATIVE_LIBRARY_VIEWS.CARD,
  );

  const permissions = useSelector(selectUserRbacPermissions);

  const hasManageBaseTemplatesPermission = useMemo(
    () => permissions.includes(PROJECT_PERMISSIONS.ManageBaseTemplates),
    [permissions],
  );

  const handleSetActiveView = (view: string) => setActiveView(view as CREATIVE_LIBRARY_VIEWS);

  return (
    <ViewContext.Provider
      value={{ activeView, setActiveView: handleSetActiveView, hasManageBaseTemplatesPermission }}
    >
      {children}
    </ViewContext.Provider>
  );
};

export const useViewContext = () => useContext(ViewContext);
